import { getPartition, getRegion, getStageName, StageName } from './env';

export interface AccountConfig {
  pipelineAccountId: string;
  portalAccountId: string;
  authAccountId: string;
}

export interface EnvironmentConfig {
  partition: string;
  region: string;
  stageName: StageName;
  appName: string;
  accounts: AccountConfig;
  midwayRoleArn: string;
  brassLambdaUrl: string;
  nexusApiUrl: string;
  /**
   * Lambda Function URL for the NexusEngine mapping-jobs API (Beta-only for now).
   * Empty string means the feature is disabled for that stage.
   */
  mappingJobsApiUrl: string;
  mappingJobsApiRegion: string;
  /**
   * Lambda Function URL for the NexusEngine framework-ingest API (Beta-only
   * for now). Empty string means the feature is disabled for that stage.
   */
  frameworkIngestApiUrl: string;
  /**
   * When true, use direct DynamoDB access instead of REST API.
   * Set to true for Prod while APIs are still being stabilized.
   */
  useDirectDynamoDB: boolean;
  /**
   * DynamoDB target for direct access. Beta points at the application
   * account's us-west-2 tables (where the ingest Lambda writes); Prod keeps
   * the legacy us-east-1 tables under the cross-account role.
   */
  dynamoDbAccountId: string;
  dynamoDbRegion: string;
  /**
   * Cross-account role to assume before reading DynamoDB. Empty string means
   * use Midway credentials directly (the Midway role already has DDB perms
   * in the target account).
   */
  dynamoDbCrossAccountRoleArn: string;
}

// BRASS Lambda Function URLs per stage
const BRASS_LAMBDA_URLS: Record<StageName, string> = {
  [StageName.Personal]: 'https://lmbaghha44n2nayuyqucsgnayu0ilqpy.lambda-url.us-west-2.on.aws/',
  [StageName.Dev]: 'https://lmbaghha44n2nayuyqucsgnayu0ilqpy.lambda-url.us-west-2.on.aws/',
  [StageName.Beta]: 'https://lmbaghha44n2nayuyqucsgnayu0ilqpy.lambda-url.us-west-2.on.aws/',
  [StageName.Gamma]: 'https://rgtzzwkkkvvp65ckwtcgnciuay0pbcyq.lambda-url.us-west-2.on.aws/',
  [StageName.Prod]: 'https://4tmhbqrmoapa5rnjstn3z6sksy0ytqvj.lambda-url.us-west-2.on.aws/',
};

// NexusEngine Mapping Jobs Lambda Function URLs per stage. Beta-only for v1;
// other stages stay empty until the feature graduates. When the URL is empty,
// isEnabled() returns false and the page shows a disabled alert.
const MAPPING_JOBS_API_URLS: Record<StageName, string> = {
  [StageName.Personal]: '',
  [StageName.Dev]: '',
  [StageName.Beta]: 'https://67h4g4ktuwusw563cxcotsb5x40faaiz.lambda-url.us-west-2.on.aws/',
  [StageName.Gamma]: '',
  [StageName.Prod]: '',
};
const MAPPING_JOBS_API_REGION = 'us-west-2';

// NexusEngine Framework Ingest Lambda Function URLs per stage. Beta-only for
// v1; other stages stay empty until the feature graduates. Empty URL means
// isEnabled() returns false and the page shows a disabled alert.
const FRAMEWORK_INGEST_API_URLS: Record<StageName, string> = {
  [StageName.Personal]: '',
  [StageName.Dev]: '',
  [StageName.Beta]: 'https://pdk57dxfbwde4ynx7mz5msgr440sbbph.lambda-url.us-west-2.on.aws/',
  [StageName.Gamma]: 'https://k7qpj6s665ylw2fyuflq3up5ga0cwoma.lambda-url.us-west-2.on.aws/',
  [StageName.Prod]: '',
};

// Nexus REST API URLs per stage.
// API paths are defined in Smithy as /v1/... and are appended by nexusApiService.
// The API stack uses custom domains and disables the default execute-api endpoint.
const NEXUS_API_URLS: Record<StageName, string> = {
  [StageName.Personal]: 'https://beta.api.compliancebeacon.sso.aws.dev',
  [StageName.Dev]: 'https://beta.api.compliancebeacon.sso.aws.dev',
  [StageName.Beta]: 'https://beta.api.compliancebeacon.sso.aws.dev',
  [StageName.Gamma]: 'https://gamma.api.compliancebeacon.sso.aws.dev',
  [StageName.Prod]: 'https://api.compliancebeacon.sso.aws.dev',
};

const BETA_ACCOUNTS: AccountConfig = {
  pipelineAccountId: '652734276074',
  portalAccountId: '728378228428', // Updated to new hosted zone account
  authAccountId: '652734276074'
};

const GAMMA_ACCOUNTS: AccountConfig = {
  pipelineAccountId: '652734276074',
  portalAccountId: '728378228428',
  authAccountId: '572372797477' // Gamma account
};

const PROD_ACCOUNTS: AccountConfig = {
  pipelineAccountId: '242108887344',
  portalAccountId: '728378228428',
  authAccountId: '242108887344' // Production account
};

const APP_NAME = 'NexusApplicationUI';

// DynamoDB target per stage. Beta reads from the application account's
// us-west-2 tables directly (same tables the ingest Lambda writes to). Prod
// still crosses into the legacy ontology account.
const DDB_TARGETS: Record<StageName, {
  accountId: string;
  region: string;
  crossAccountRoleArn: string;
}> = {
  [StageName.Personal]: {
    accountId: '652734276074',
    region: 'us-west-2',
    crossAccountRoleArn: '',
  },
  [StageName.Dev]: {
    accountId: '652734276074',
    region: 'us-west-2',
    crossAccountRoleArn: '',
  },
  [StageName.Beta]: {
    accountId: '652734276074',
    region: 'us-west-2',
    crossAccountRoleArn: '',
  },
  [StageName.Gamma]: {
    accountId: '572372797477',
    region: 'us-west-2',
    crossAccountRoleArn: '',
  },
  [StageName.Prod]: {
    accountId: '305345571965',
    region: 'us-east-1',
    crossAccountRoleArn: 'arn:aws:iam::305345571965:role/NexusApplicationUI-DynamoDBAccess-TEMP',
  },
};

export const DEV_ENVIRONMENT_CONFIG: EnvironmentConfig = {
  partition: 'aws',
  region: getRegion(),
  stageName: StageName.Personal,
  appName: APP_NAME,
  accounts: BETA_ACCOUNTS,
  midwayRoleArn: `arn:aws:iam::${BETA_ACCOUNTS.authAccountId}:role/MidwayAuthRole-dev-${APP_NAME}`,
  brassLambdaUrl: BRASS_LAMBDA_URLS[StageName.Personal],
  nexusApiUrl: NEXUS_API_URLS[StageName.Personal],
  mappingJobsApiUrl: MAPPING_JOBS_API_URLS[StageName.Personal],
  mappingJobsApiRegion: MAPPING_JOBS_API_REGION,
  frameworkIngestApiUrl: FRAMEWORK_INGEST_API_URLS[StageName.Personal],
  // Route reads/writes through the server-side Nexus REST API so authorization
  // (framework visibility, review integrity, feedback ownership) is enforced by
  // the backend authorizers instead of client-side JavaScript.
  useDirectDynamoDB: false,
  dynamoDbAccountId: DDB_TARGETS[StageName.Personal].accountId,
  dynamoDbRegion: DDB_TARGETS[StageName.Personal].region,
  dynamoDbCrossAccountRoleArn: DDB_TARGETS[StageName.Personal].crossAccountRoleArn,
};

export const ENVIRONMENTS: Record<StageName, EnvironmentConfig> = {
  [StageName.Personal]: DEV_ENVIRONMENT_CONFIG,
  [StageName.Dev]: {
    partition: 'aws',
    region: getRegion(),
    stageName: StageName.Dev,
    appName: APP_NAME,
    accounts: BETA_ACCOUNTS,
    midwayRoleArn: `arn:aws:iam::${BETA_ACCOUNTS.authAccountId}:role/MidwayAuthRole-dev-${APP_NAME}`,
    brassLambdaUrl: BRASS_LAMBDA_URLS[StageName.Dev],
    nexusApiUrl: NEXUS_API_URLS[StageName.Dev],
    mappingJobsApiUrl: MAPPING_JOBS_API_URLS[StageName.Dev],
    mappingJobsApiRegion: MAPPING_JOBS_API_REGION,
    frameworkIngestApiUrl: FRAMEWORK_INGEST_API_URLS[StageName.Dev],
    // Route through the server-side Nexus REST API so authorization is enforced
    // by backend authorizers instead of client-side JavaScript.
    useDirectDynamoDB: false,
    dynamoDbAccountId: DDB_TARGETS[StageName.Dev].accountId,
    dynamoDbRegion: DDB_TARGETS[StageName.Dev].region,
    dynamoDbCrossAccountRoleArn: DDB_TARGETS[StageName.Dev].crossAccountRoleArn,
  },
  [StageName.Beta]: {
    partition: 'aws',
    region: getRegion(),
    stageName: StageName.Beta,
    appName: APP_NAME,
    accounts: BETA_ACCOUNTS,
    midwayRoleArn: `arn:aws:iam::${BETA_ACCOUNTS.authAccountId}:role/MidwayAuthRole-beta-${APP_NAME}`,
    brassLambdaUrl: BRASS_LAMBDA_URLS[StageName.Beta],
    nexusApiUrl: NEXUS_API_URLS[StageName.Beta],
    mappingJobsApiUrl: MAPPING_JOBS_API_URLS[StageName.Beta],
    mappingJobsApiRegion: MAPPING_JOBS_API_REGION,
    frameworkIngestApiUrl: FRAMEWORK_INGEST_API_URLS[StageName.Beta],
    // Route through the server-side Nexus REST API so authorization is enforced
    // by backend authorizers instead of client-side JavaScript.
    useDirectDynamoDB: false,
    dynamoDbAccountId: DDB_TARGETS[StageName.Beta].accountId,
    dynamoDbRegion: DDB_TARGETS[StageName.Beta].region,
    dynamoDbCrossAccountRoleArn: DDB_TARGETS[StageName.Beta].crossAccountRoleArn,
  },
  [StageName.Gamma]: {
    partition: 'aws',
    region: getRegion(),
    stageName: StageName.Gamma,
    appName: APP_NAME,
    accounts: GAMMA_ACCOUNTS,
    midwayRoleArn: `arn:aws:iam::${GAMMA_ACCOUNTS.authAccountId}:role/MidwayAuthRole-gamma-${APP_NAME}`,
    brassLambdaUrl: BRASS_LAMBDA_URLS[StageName.Gamma],
    nexusApiUrl: NEXUS_API_URLS[StageName.Gamma],
    mappingJobsApiUrl: MAPPING_JOBS_API_URLS[StageName.Gamma],
    mappingJobsApiRegion: MAPPING_JOBS_API_REGION,
    frameworkIngestApiUrl: FRAMEWORK_INGEST_API_URLS[StageName.Gamma],
    // Route through the server-side Nexus REST API so authorization is enforced
    // by backend authorizers instead of client-side JavaScript.
    useDirectDynamoDB: false,
    dynamoDbAccountId: DDB_TARGETS[StageName.Gamma].accountId,
    dynamoDbRegion: DDB_TARGETS[StageName.Gamma].region,
    dynamoDbCrossAccountRoleArn: DDB_TARGETS[StageName.Gamma].crossAccountRoleArn,
  },
  [StageName.Prod]: {
    partition: 'aws',
    region: getRegion(),
    stageName: StageName.Prod,
    appName: APP_NAME,
    accounts: PROD_ACCOUNTS,
    midwayRoleArn: `arn:aws:iam::${PROD_ACCOUNTS.authAccountId}:role/MidwayAuthRole-prod-${APP_NAME}`,
    brassLambdaUrl: BRASS_LAMBDA_URLS[StageName.Prod],
    nexusApiUrl: NEXUS_API_URLS[StageName.Prod],
    mappingJobsApiUrl: MAPPING_JOBS_API_URLS[StageName.Prod],
    mappingJobsApiRegion: MAPPING_JOBS_API_REGION,
    frameworkIngestApiUrl: FRAMEWORK_INGEST_API_URLS[StageName.Prod],
    // Route through the server-side Nexus REST API so authorization is enforced
    // by backend authorizers instead of client-side JavaScript.
    useDirectDynamoDB: false,
    dynamoDbAccountId: DDB_TARGETS[StageName.Prod].accountId,
    dynamoDbRegion: DDB_TARGETS[StageName.Prod].region,
    dynamoDbCrossAccountRoleArn: DDB_TARGETS[StageName.Prod].crossAccountRoleArn,
  }
};

export function getEnvironmentConfig(): EnvironmentConfig {
  const stageName = getStageName();
  
  if (stageName === StageName.Personal) {
    return DEV_ENVIRONMENT_CONFIG;
  }
  
  const config = ENVIRONMENTS[stageName];
  
  if (!config) {
    throw new Error(`Unknown environment stage: ${stageName}`);
  }
  
  return config;
}
